import { useEffect, useState } from "react";
import { Link } from "@tanstack/react-router";
import { OPEN_PREFERENCES_EVENT, applyConsent, readConsent, saveConsent } from "@/lib/consent";

export function CookieBanner() {
  const [visible, setVisible] = useState(false);
  const [managing, setManaging] = useState(false);
  const [analytics, setAnalytics] = useState(true);
  const [marketing, setMarketing] = useState(true);

  useEffect(() => {
    const stored = readConsent();
    if (stored) {
      applyConsent(stored);
      setAnalytics(stored.analytics);
      setMarketing(stored.marketing);
    } else {
      setVisible(true);
    }

    const open = () => {
      const current = readConsent();
      setAnalytics(current?.analytics ?? true);
      setMarketing(current?.marketing ?? true);
      setManaging(true);
      setVisible(true);
    };
    window.addEventListener(OPEN_PREFERENCES_EVENT, open);
    return () => window.removeEventListener(OPEN_PREFERENCES_EVENT, open);
  }, []);

  function decide(next: { analytics: boolean; marketing: boolean }) {
    saveConsent(next);
    setVisible(false);
    setManaging(false);
  }

  if (!visible) return null;

  return (
    <div
      role="dialog"
      aria-label="Preferências de cookies"
      className="fixed bottom-4 right-4 left-4 z-[60] sm:left-auto sm:w-80"
    >
      <div className="rounded-sm border border-border bg-card/95 p-4 shadow-card backdrop-blur">
        <p className="text-[0.65rem] font-semibold uppercase tracking-[0.18em] text-nook-green">
          Cookies
        </p>
        <p className="mt-2 text-xs leading-5 text-muted-foreground">
          Usamos cookies para melhorar sua experiência. Veja nossa{" "}
          <Link to="/politica-de-privacidade" className="text-nook-green hover:underline">
            Política de Privacidade
          </Link>
          .
        </p>

        {managing && (
          <div className="mt-3 space-y-2 border-t border-border pt-3">
            <Row
              title="Necessários"
              description="Funcionamento e segurança do site."
              checked
              disabled
            />
            <Row
              title="Analíticos"
              description="Entender como o site é usado."
              checked={analytics}
              onChange={setAnalytics}
            />
            <Row
              title="Marketing"
              description="Medição de campanhas."
              checked={marketing}
              onChange={setMarketing}
            />
          </div>
        )}

        <div className="mt-3 flex flex-col gap-2">
          {!managing && (
            <button
              type="button"
              onClick={() => setManaging(true)}
              className="inline-flex h-8 items-center justify-center rounded-sm border border-border px-3 text-[0.6rem] font-semibold uppercase tracking-[0.14em] text-foreground hover:border-nook-green hover:text-nook-green"
            >
              Gerenciar
            </button>
          )}
          <button
            type="button"
            onClick={() => decide({ analytics: false, marketing: false })}
            className="inline-flex h-8 items-center justify-center rounded-sm border border-border px-3 text-[0.6rem] font-semibold uppercase tracking-[0.14em] text-foreground hover:border-nook-green hover:text-nook-green"
          >
            Rejeitar
          </button>
          {managing ? (
            <button
              type="button"
              onClick={() => decide({ analytics, marketing })}
              className="inline-flex h-8 items-center justify-center rounded-sm bg-nook-green px-3 text-[0.6rem] font-semibold uppercase tracking-[0.14em] text-nook-green-foreground"
            >
              Salvar
            </button>
          ) : (
            <button
              type="button"
              onClick={() => decide({ analytics: true, marketing: true })}
              className="inline-flex h-8 items-center justify-center rounded-sm bg-nook-green px-3 text-[0.6rem] font-semibold uppercase tracking-[0.14em] text-nook-green-foreground"
            >
              Aceitar
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

function Row({
  title,
  description,
  checked,
  disabled,
  onChange,
}: {
  title: string;
  description: string;
  checked: boolean;
  disabled?: boolean;
  onChange?: (v: boolean) => void;
}) {
  return (
    <label className="flex items-start justify-between gap-3">
      <span>
        <span className="block text-xs font-semibold text-foreground">{title}</span>
        <span className="mt-0.5 block text-[0.65rem] leading-4 text-muted-foreground">{description}</span>
      </span>
      <input
        type="checkbox"
        checked={checked}
        disabled={disabled}
        onChange={(e) => onChange?.(e.target.checked)}
        className="mt-0.5 size-3.5 shrink-0 accent-nook-green disabled:opacity-50"
        aria-label={title}
      />
    </label>
  );
}
